import { Injectable, computed, signal, NgZone, inject } from '@angular/core';
import { chatWsAutoReconnect, chatWsBaseUrl } from '../../../env';

export interface TwitchMessage {
  id?: number | string;
  username?: string;
  message: string;
  timestamp?: string | number;
  /** Source platform of the message. The backend now tags YouTube-stream
   *  messages explicitly; anything else (incl. absent) is treated as Twitch,
   *  preserving the previous single-platform behaviour. */
  platform?: 'youtube' | 'twitch';
  /** Stable, monotonic, client-assigned key for @for trackBy. Never reuses
   *  the array index, so trimming the leading item does not re-create the
   *  whole row. Set once at ingestion; deduplication still keys on `id`. */
  trackId?: number;
}

/** Hard safety backstop on stored messages. The marquee force-trims the DOM
 *  far below this (and compensates its scroll offset), so this only matters if
 *  the controller is ever not running. Keep it comfortably above the marquee's
 *  SOFT_CAP. */
const MAX_STORED_MESSAGES = 60;

/** How many history messages to show when a source (re)connects — e.g. an OBS
 *  scene being toggled back on. Kept small so switching scenes shows just a few
 *  recent messages instead of flooding with the whole backlog. */
const INITIAL_HISTORY_LIMIT = 5;

/** Sample data for `?testMode=` — varied lengths to exercise the 2-line clamp. */
const TEST_USERS = [
  'NightOwl', 'cryptoKing', 'sara_88', 'MOON_BOY', 'trader_jane', 'xX_VIPER_Xx',
  'LunaTV', 'grindset', 'pixel_pete', 'VANGUARD', 'degenDan', 'q_t_pie',
];
const TEST_TEXTS = [
  'LETS GOOO',
  'gm everyone, what are we trading today?',
  'that entry was clean 🔥',
  'KEKW',
  'is the giveaway still live??',
  'PogChamp this stream is on fire today, keep it up team',
  'wen moon',
  'I called it at 42k, you all owe me lunch — also great macro breakdown',
  'first',
  'W',
  'how do I join the discord',
  'this overlay is so smooth now ngl',
];

@Injectable({
  providedIn: 'root'
})
export class ChatActionsService {
  private readonly zone = inject(NgZone);
  private alertsSignal1 = signal<TwitchMessage[]>([]);
  private alertsSignal2 = signal<TwitchMessage[]>([]);
  private readonlyAlerts1 = computed(() => this.alertsSignal1());
  private readonlyAlerts2 = computed(() => this.alertsSignal2());
  private websocket1?: WebSocket;
  private websocket2?: WebSocket;
  private reconnectTimer1: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer2: ReturnType<typeof setTimeout> | null = null;
  /** Reconnect if NO inbound traffic (ping OR message) arrives within this
   *  window — catches half-open/silent-death sockets. Server pings ~every 25s. */
  private serverPingWatchdog1: ReturnType<typeof setTimeout> | null = null;
  private serverPingWatchdog2: ReturnType<typeof setTimeout> | null = null;
  /** Reconnect if no REAL chat frame (initial/messages) arrives within this
   *  window. Unlike the ping watchdog it is NOT reset by server pings, so it
   *  catches the "socket alive, pings fine, but the backend stopped forwarding
   *  Twitch chat" failure (e.g. the backend's Twitch token/session expiring
   *  after ~24h) — which the ping watchdog can never see. Reopening the socket
   *  makes the backend re-subscribe to Twitch, exactly like a page refresh. The
   *  stream's bot posts periodic messages, so a healthy chat is never silent
   *  this long; a false trigger (genuinely quiet chat) is harmless — it just
   *  reconnects. */
  private messageWatchdog1: ReturnType<typeof setTimeout> | null = null;
  private messageWatchdog2: ReturnType<typeof setTimeout> | null = null;
  private readonly activeSessions = new Set<number>();
  private stopRequested1 = false;
  private stopRequested2 = false;
  private readonly reconnectDelayMs = 3000;
  private readonly serverPingTimeoutMs = 80_000;
  /** No real chat frame for this long (pings ignored) → reconnect. The stream's
   *  bot posts periodically, so a healthy chat is never silent this long. */
  private readonly messageLivenessTimeoutMs = 45 * 60 * 1000; // 45 min
  private readonly healthCheckIntervalMs = 10_000;
  private trackCounter = 0;
  private testCounter = 0;
  private readonly testTimers = new Map<number, ReturnType<typeof setInterval>[]>();
  private readonly testAccumulator = new Map<number, number>();
  private messageBuffer1: TwitchMessage[] = [];
  private messageBuffer2: TwitchMessage[] = [];
  private bufferTimer1: ReturnType<typeof setTimeout> | null = null;
  private bufferTimer2: ReturnType<typeof setTimeout> | null = null;
  private healthCheckTimer: ReturnType<typeof setInterval> | null = null;
  private lifecycleHandlersBound = false;
  private connectingStartedAt1: number | null = null;
  private connectingStartedAt2: number | null = null;
  private readonly connectingTimeoutMs = 15_000;

  constructor() {}

  private ensureRuntimeInitialized(): void {
    if (this.healthCheckTimer) return;

    this.bindPageLifecycleHandlers();
    this.healthCheckTimer = setInterval(() => {
      for (const session of this.activeSessions) {
        this.ensureSessionConnected(session);
      }
    }, this.healthCheckIntervalMs);
  }

  public startAlertsPolling(session: number = 1, _intervalTime: number = 7000): void {
    this.ensureRuntimeInitialized();
    this.activeSessions.add(session);
    this.ensureSessionConnected(session);
  }

  // ── Test mode (?testMode=1&rate=&burst=&burstEvery=) ───────────────────────
  // Feeds synthetic messages through the exact same render pipeline as the
  // WebSocket, so the marquee can be stress-tested (steady stream + bursts)
  // without a backend. Does NOT open a socket.

  public startTestMode(
    session: number,
    opts: { rate: number; burst: number; burstEveryMs: number },
  ): void {
    this.stopTestMode(session);
    const timers: ReturnType<typeof setInterval>[] = [];
    this.testAccumulator.set(session, 0);

    // Steady stream. Fractional accumulator hits any `rate` (msgs/sec) on
    // average; the 120ms cadence stays above the 100ms append-buffer debounce
    // so it always flushes.
    if (opts.rate > 0) {
      const tickMs = 120;
      timers.push(
        setInterval(() => {
          const acc = (this.testAccumulator.get(session) ?? 0) + (opts.rate * tickMs) / 1000;
          const n = Math.floor(acc);
          this.testAccumulator.set(session, acc - n);
          if (n > 0) {
            this.addMessagesToBuffer(this.makeFakeMessages(n), session);
          }
        }, tickMs),
      );
    }

    // Periodic burst — exercises the eased speed-up without a visible jerk.
    if (opts.burst > 0) {
      timers.push(
        setInterval(() => {
          this.addMessagesToBuffer(this.makeFakeMessages(opts.burst), session);
        }, opts.burstEveryMs),
      );
    }

    this.testTimers.set(session, timers);
  }

  public stopTestMode(session: number): void {
    const timers = this.testTimers.get(session);
    if (timers) {
      for (const t of timers) clearInterval(t);
      this.testTimers.delete(session);
    }
    this.testAccumulator.delete(session);
  }

  private makeFakeMessages(count: number): TwitchMessage[] {
    const out: TwitchMessage[] = [];
    for (let i = 0; i < count; i++) {
      out.push({
        id: `test-${++this.testCounter}`,
        username: TEST_USERS[Math.floor(Math.random() * TEST_USERS.length)],
        message: TEST_TEXTS[Math.floor(Math.random() * TEST_TEXTS.length)],
        timestamp: Date.now(),
        platform: Math.random() < 0.4 ? 'youtube' : 'twitch',
      });
    }
    return out;
  }

  /** Reconnect after OBS scene hide/show or browser source resume. */
  public ensureSessionConnected(session: number): void {
    this.activeSessions.add(session);
    this.setStopRequested(session, false);

    const socket = this.getSocket(session);
    if (socket?.readyState === WebSocket.OPEN) {
      return;
    }

    if (socket?.readyState === WebSocket.CONNECTING) {
      const startedAt = this.getConnectingStartedAt(session);
      if (startedAt && Date.now() - startedAt < this.connectingTimeoutMs) {
        return;
      }
      console.warn(`[live-chat${session}] stale CONNECTING socket, forcing reconnect`);
    }

    this.clearReconnectTimer(session);
    this.closeSocket(session);
    this.connectSocket(session);
  }

  private closeSocket(session: number): void {
    const socket = this.getSocket(session);
    if (!socket) return;

    socket.onclose = null;
    socket.onerror = null;
    socket.onmessage = null;
    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      socket.close();
    }
    this.setSocket(session, undefined);
    this.setConnectingStartedAt(session, null);
  }

  private getConnectingStartedAt(session: number): number | null {
    return session === 1 ? this.connectingStartedAt1 : this.connectingStartedAt2;
  }

  private setConnectingStartedAt(session: number, value: number | null): void {
    if (session === 1) {
      this.connectingStartedAt1 = value;
    } else {
      this.connectingStartedAt2 = value;
    }
  }

  private bindPageLifecycleHandlers(): void {
    if (this.lifecycleHandlersBound || typeof document === 'undefined') return;
    this.lifecycleHandlersBound = true;

    const resumeSessions = () => {
      for (const session of this.activeSessions) {
        this.ensureSessionConnected(session);
      }
    };

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        resumeSessions();
      }
    });
    window.addEventListener('focus', resumeSessions);
    window.addEventListener('pageshow', resumeSessions);
    window.addEventListener('online', resumeSessions);
  }

  private connectSocket(session: number): void {
    this.clearReconnectTimer(session);
    this.stopHeartbeat(session);

    const url = this.getWebSocketUrl(session);
    console.log(`[live-chat${session}] connecting to ${url}`);
    this.setConnectingStartedAt(session, Date.now());
    const socket = new WebSocket(url);
    this.setSocket(session, socket);

    socket.onopen = () => {
      this.setConnectingStartedAt(session, null);
      console.log(`[live-chat${session}] connected`);
      this.startHeartbeat(session);
    };

    socket.onmessage = (event) => {
      this.handleSocketMessage(event.data, session);
    };

    socket.onerror = (error) => {
      console.error(`Websocket error for live-chat${session}:`, error);
    };

    socket.onclose = () => {
      this.setConnectingStartedAt(session, null);
      this.stopHeartbeat(session);
      this.setSocket(session, undefined);
      if (this.isStopRequested(session)) return;
      if (!chatWsAutoReconnect) return;
      this.scheduleReconnect(session);
    };
  }

  private startHeartbeat(session: number): void {
    this.resetServerPingWatchdog(session);
    this.resetMessageWatchdog(session);
  }

  private stopHeartbeat(session: number): void {
    this.clearServerPingWatchdog(session);
    this.clearMessageWatchdog(session);
  }

  private resetServerPingWatchdog(session: number): void {
    this.clearServerPingWatchdog(session);

    const timeout = setTimeout(() => {
      console.warn(
        `No server ping within ${this.serverPingTimeoutMs}ms for live-chat${session}, reconnecting`,
      );
      this.forceReconnect(session);
    }, this.serverPingTimeoutMs);

    if (session === 1) {
      this.serverPingWatchdog1 = timeout;
    } else {
      this.serverPingWatchdog2 = timeout;
    }
  }

  private clearServerPingWatchdog(session: number): void {
    const timeout = session === 1 ? this.serverPingWatchdog1 : this.serverPingWatchdog2;
    if (!timeout) return;

    clearTimeout(timeout);
    if (session === 1) {
      this.serverPingWatchdog1 = null;
    } else {
      this.serverPingWatchdog2 = null;
    }
  }

  /** Reset on every real chat frame (initial/messages) — see the field doc.
   *  Deliberately NOT reset by pings, so an alive-but-silent socket is caught. */
  private resetMessageWatchdog(session: number): void {
    this.clearMessageWatchdog(session);

    const timeout = setTimeout(() => {
      console.warn(
        `No chat messages within ${this.messageLivenessTimeoutMs}ms for live-chat${session} ` +
        `(socket still alive) — reconnecting to force the backend to re-subscribe.`,
      );
      this.forceReconnect(session);
    }, this.messageLivenessTimeoutMs);

    if (session === 1) {
      this.messageWatchdog1 = timeout;
    } else {
      this.messageWatchdog2 = timeout;
    }
  }

  private clearMessageWatchdog(session: number): void {
    const timeout = session === 1 ? this.messageWatchdog1 : this.messageWatchdog2;
    if (!timeout) return;

    clearTimeout(timeout);
    if (session === 1) {
      this.messageWatchdog1 = null;
    } else {
      this.messageWatchdog2 = null;
    }
  }

  private forceReconnect(session: number): void {
    if (this.isStopRequested(session)) return;

    this.stopHeartbeat(session);
    this.clearReconnectTimer(session);

    const socket = this.getSocket(session);
    if (socket) {
      this.closeSocket(session);
    }
    this.setSocket(session, undefined);

    if (!chatWsAutoReconnect) return;
    this.scheduleReconnect(session);
  }

  private addMessagesToBuffer(messages: TwitchMessage[], session: number, replace = false): void {
    if (replace) {
      this.appendMessages(session, messages, true);
      return;
    }

    const buffer = session === 1 ? this.messageBuffer1 : this.messageBuffer2;
    buffer.push(...messages);

    const timer = session === 1 ? this.bufferTimer1 : this.bufferTimer2;
    if (timer) {
      clearTimeout(timer);
    }

    const newTimer = setTimeout(() => {
      this.flushMessageBuffer(session);
    }, 100);

    if (session === 1) {
      this.bufferTimer1 = newTimer;
    } else {
      this.bufferTimer2 = newTimer;
    }
  }

  private appendMessages(session: number, incoming: TwitchMessage[], replace = false): void {
    const apply = (prev: TwitchMessage[]) => {
      const base = replace ? [] : prev;
      const seen = new Set<string>();
      for (const item of base) {
        const key = this.messageKey(item);
        if (key) seen.add(key);
      }

      const uniqueIncoming = incoming.filter((item, index) => {
        const key = this.messageKey(item, index);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      if (uniqueIncoming.length === 0) return prev;

      // Stamp a stable trackBy key (after dedup, which keys on `id`).
      for (const item of uniqueIncoming) {
        if (item.trackId === undefined) item.trackId = ++this.trackCounter;
      }

      const next = [...base, ...uniqueIncoming];
      // Safety backstop only — the marquee force-trims well below this (and
      // compensates its scroll offset). This cap should never bite in practice.
      return next.length > MAX_STORED_MESSAGES
        ? next.slice(next.length - MAX_STORED_MESSAGES)
        : next;
    };

    this.zone.run(() => {
      if (session === 1) {
        this.alertsSignal1.update(apply);
      } else {
        this.alertsSignal2.update(apply);
      }
    });
  }

  private messageKey(message: TwitchMessage, index = 0): string {
    if (message.id !== undefined && message.id !== null) {
      return String(message.id);
    }
    return `${message.username ?? 'user'}-${message.message}-${message.timestamp ?? index}`;
  }

  public trimLeadingMessage(session: number = 1): boolean {
    const current = session === 1 ? this.alertsSignal1() : this.alertsSignal2();
    if (current.length <= 8) return false;

    this.zone.run(() => {
      if (session === 1) {
        this.alertsSignal1.set(current.slice(1));
      } else {
        this.alertsSignal2.set(current.slice(1));
      }
    });
    return true;
  }

  /** Called when a hidden OBS source becomes visible again. Keeps the `head`
   *  cards that were already on screen and drops the backlog that piled up
   *  while hidden EXCEPT its newest `INITIAL_HISTORY_LIMIT` — those then scroll
   *  in from the right with the normal animation, while the on-screen messages
   *  stay put (continuity, no flood). */
  public keepHeadAndRecentHistory(session: number, head: number): void {
    const current = session === 1 ? this.alertsSignal1() : this.alertsSignal2();
    if (current.length <= head + INITIAL_HISTORY_LIMIT) return;
    const kept = [
      ...current.slice(0, head),
      ...current.slice(current.length - INITIAL_HISTORY_LIMIT),
    ];
    this.zone.run(() => {
      if (session === 1) {
        this.alertsSignal1.set(kept);
      } else {
        this.alertsSignal2.set(kept);
      }
    });
  }

  private flushMessageBuffer(session: number, replace = false): void {
    const buffer = session === 1 ? this.messageBuffer1 : this.messageBuffer2;
    if (buffer.length === 0) return;

    this.appendMessages(session, buffer, replace);

    if (session === 1) {
      this.messageBuffer1 = [];
      this.bufferTimer1 = null;
    } else {
      this.messageBuffer2 = [];
      this.bufferTimer2 = null;
    }
  }

  private handleSocketMessage(rawData: string, session: number): void {
    // Any inbound frame proves the connection is alive — reset the liveness
    // watchdog on every message, not only on server pings. Otherwise a busy
    // chat whose pings lag past the timeout would be reconnected needlessly.
    this.resetServerPingWatchdog(session);

    let payload: unknown;

    try {
      payload = JSON.parse(rawData);
    } catch (error) {
      console.error(`Failed to parse websocket payload for session ${session}:`, error);
      return;
    }

    if (!payload || typeof payload !== 'object') return;

    const event = payload as { type?: string; updated_count?: number };

    const eventType = typeof event.type === 'string' ? event.type.toLowerCase() : '';

    if (eventType === 'ping') {
      this.sendPong(session);
      return;
    }

    if (eventType === 'mark_read_ok') {
      return;
    }

    if (eventType !== 'initial' && eventType !== 'messages') {
      return;
    }

    const messages = this.extractMessages(payload, 0);
    if (messages.length === 0) return;

    // A real chat frame proves the backend is still forwarding Twitch on THIS
    // socket — keep the liveness watchdog from firing. (Ping frames don't; they
    // returned above, so an alive-but-silent socket still times out.)
    this.resetMessageWatchdog(session);

    console.log(`[live-chat${session}] received ${messages.length} message(s), type=${eventType}`);

    const capped =
      eventType === 'initial'
        ? messages.slice(-INITIAL_HISTORY_LIMIT)
        : messages;

    this.addMessagesToBuffer(capped, session, eventType === 'initial');
    this.markAsRead(capped, session);
  }

  private sendPong(session: number): void {
    const socket = this.getSocket(session);
    if (!socket || socket.readyState !== WebSocket.OPEN) return;

    try {
      socket.send(JSON.stringify({ type: 'pong' }));
    } catch {
      // ping watchdog will reconnect if needed
    }
  }

  private extractMessages(payload: unknown, depth: number): TwitchMessage[] {
    if (depth > 3) return [];

    if (Array.isArray(payload)) {
      return payload.map((message) => this.normalizeMessage(message)).filter(Boolean) as TwitchMessage[];
    }

    const event = payload as {
      messages?: unknown;
      entries?: unknown;
      items?: unknown;
      data?: unknown;
      message?: unknown;
      payload?: unknown;
    };

    for (const key of ['messages', 'entries', 'items'] as const) {
      const list = event[key];
      if (Array.isArray(list)) {
        return list.map((message) => this.normalizeMessage(message)).filter(Boolean) as TwitchMessage[];
      }
    }

    if (Array.isArray(event.data)) {
      return event.data.map((message) => this.normalizeMessage(message)).filter(Boolean) as TwitchMessage[];
    }

    if (event.data && typeof event.data === 'object') {
      const nested = event.data as { messages?: unknown; entries?: unknown; items?: unknown };
      for (const key of ['messages', 'entries', 'items'] as const) {
        const list = nested[key];
        if (Array.isArray(list)) {
          return list.map((message) => this.normalizeMessage(message)).filter(Boolean) as TwitchMessage[];
        }
      }
    }

    if (event.payload && typeof event.payload === 'object' && event.payload !== payload) {
      return this.extractMessages(event.payload, depth + 1);
    }

    const single = this.normalizeMessage(event.message ?? event.data);
    return single ? [single] : [];
  }

  private readString(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return '';
  }

  private normalizeMessage(raw: unknown): TwitchMessage | null {
    if (!raw || typeof raw !== 'object') return null;

    const source = raw as Record<string, unknown>;
    const message = this.readString(source, ['message', 'text', 'chatmessage', 'body', 'content']);

    if (!message) return null;

    const username = this.readString(source, ['username', 'chatname', 'user', 'name']) || 'Unknown';

    const id =
      source['id']
      ?? source['message_id']
      ?? source['mid']
      ?? source['messageId'];

    const timestamp = source['timestamp'] ?? source['created_at'] ?? source['createdAt'];

    // The backend tags YouTube messages; treat everything else as Twitch so the
    // existing card design is unchanged for Twitch and any untagged message.
    const platformRaw = this.readString(source, ['platform', 'type', 'source']).toLowerCase();
    const platform: 'youtube' | 'twitch' =
      platformRaw === 'youtube' || platformRaw === 'yt' ? 'youtube' : 'twitch';

    return {
      id: id as number | string | undefined,
      username,
      message,
      timestamp: timestamp as string | number | undefined,
      platform,
    };
  }

  private getWebSocketUrl(session: number): string {
    return `${chatWsBaseUrl}/ws/overlay/twitch-messages/${session}/`;
  }

  private scheduleReconnect(session: number): void {
    this.clearReconnectTimer(session);

    const timer = setTimeout(() => {
      if (this.isStopRequested(session)) return;
      this.connectSocket(session);
    }, this.reconnectDelayMs);

    if (session === 1) {
      this.reconnectTimer1 = timer;
    } else {
      this.reconnectTimer2 = timer;
    }
  }

  private clearReconnectTimer(session: number): void {
    const timer = session === 1 ? this.reconnectTimer1 : this.reconnectTimer2;
    if (!timer) return;

    clearTimeout(timer);
    if (session === 1) {
      this.reconnectTimer1 = null;
    } else {
      this.reconnectTimer2 = null;
    }
  }

  private getSocket(session: number): WebSocket | undefined {
    return session === 1 ? this.websocket1 : this.websocket2;
  }

  private setSocket(session: number, socket: WebSocket | undefined): void {
    if (session === 1) {
      this.websocket1 = socket;
    } else {
      this.websocket2 = socket;
    }
  }

  private isStopRequested(session: number): boolean {
    return session === 1 ? this.stopRequested1 : this.stopRequested2;
  }

  private setStopRequested(session: number, value: boolean): void {
    if (session === 1) {
      this.stopRequested1 = value;
    } else {
      this.stopRequested2 = value;
    }
  }

  public stopAlertsPolling(session: number = 1): void {
    this.activeSessions.delete(session);

    const timer = session === 1 ? this.bufferTimer1 : this.bufferTimer2;
    if (timer) {
      clearTimeout(timer);
      this.flushMessageBuffer(session);
    }

    this.setStopRequested(session, true);
    this.clearReconnectTimer(session);
    this.stopHeartbeat(session);

    const socket = this.getSocket(session);
    if (socket) {
      socket.onclose = null;
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
    }
    this.setSocket(session, undefined);
  }

  public clearMessages(session: number = 1): void {
    if (session === 1) {
      this.alertsSignal1.set([]);
    } else {
      this.alertsSignal2.set([]);
    }
  }

  public removeFirst(session: number = 1) {
    const current = session === 1 ? this.alertsSignal1() : this.alertsSignal2();
    if (current.length > 0) {
      if (session === 1) {
        this.alertsSignal1.set(current.slice(1));
      } else {
        this.alertsSignal2.set(current.slice(1));
      }
    }
  }

  getAlerts(session: number = 1) {
    return session === 1 ? this.readonlyAlerts1 : this.readonlyAlerts2;
  }

  getMessageCount(session: number = 1): number {
    return session === 1 ? this.alertsSignal1().length : this.alertsSignal2().length;
  }

  public markAsRead(alerts: TwitchMessage[], session: number = 1) {
    const ids = alerts
      .filter((a) => a.id !== undefined && a.id !== null)
      .map(a => a.id);

    if (ids.length === 0) return;

    const socket = this.getSocket(session);
    if (!socket || socket.readyState !== WebSocket.OPEN) return;

    socket.send(JSON.stringify({ type: 'mark_read', message_ids: ids }));
  }
}
